import { DialogContentText, DialogProps, Typography } from '@mui/material';
import { ReactNode } from 'react';

import Dialog, { DialogAction } from '../Dialog';

interface ConfirmationDialogProps {
    open: boolean;
    /** Undefined makes the dialog undismissable (no Escape, backdrop or X), e.g. while its action is in flight. */
    handleClose?: () => void;
    accept: DialogAction;
    reject?: DialogAction;
    message: string | ReactNode;
    title: string;
    /** Forwarded to the underlying MUI Dialog — use this to bump z-index above parent drawers. */
    sx?: DialogProps['sx'];
    /** Widen past the default for content laid out side by side. */
    maxWidth?: DialogProps['maxWidth'];
}

function ConfirmationDialog({
    open,
    handleClose,
    accept,
    reject,
    message,
    title,
    sx,
    maxWidth = 'sm',
}: ConfirmationDialogProps) {
    return (
        <Dialog
            open={open}
            onClose={handleClose}
            heading={<Typography variant="h3">{title}</Typography>}
            maxWidth={maxWidth}
            fullWidth
            actionButtonsSize="small"
            sx={sx}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: 3,
                },
            }}
            componentProps={{
                dialogContent: {
                    sx: {
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    },
                },
                dialogActions: {
                    sx: {
                        py: 2,
                        px: 3,
                        gap: 1,
                    },
                },
            }}
            negativeAction={
                reject
                    ? {
                          label: reject.label,
                          onClick: reject.onClick,
                          variant: reject.variant,
                          color: reject.color,
                          loading: reject.loading,
                          disabled: reject.disabled,
                      }
                    : undefined
            }
            positiveAction={{
                label: accept.label,
                variant: accept.variant,
                color: accept.color,
                onClick: accept.onClick,
                loading: accept.loading,
                disabled: accept.disabled,
            }}>
            {typeof message === 'string' ? (
                <DialogContentText sx={{ whiteSpace: 'pre-line' }}>{message}</DialogContentText>
            ) : (
                message
            )}
        </Dialog>
    );
}

export default ConfirmationDialog;
